import type { Booking } from '../types/booking';
import { deviceTimeZone } from './listening';

/**
 * Booking FORMATTING and the join window: the ONE source for every booking
 * time the app shows (the slot list, the Book button, the booked-call page,
 * Upcoming, the "Opens at" line).
 *
 * The rule (David, 2026-10-30): every time shown to a person names its zone.
 * In the app that zone is the DEVICE's: the clock the viewer is living by,
 * which can differ from the zone stored on the booking (booked at home,
 * opened while travelling). The stored zone stays the zone of record for
 * the server's emails, pushes and calendar files (#1154), and is the
 * fallback here only when the device cannot name its zone; with neither,
 * the time is UTC and says "UTC". Every formatter below takes a `ShownZone`,
 * which only `shownZone()` makes, so a caller cannot hand one the stored
 * zone by mistake. Hours that are NOT an instant (a listener's weekly
 * schedule) are never converted: they are labelled in their own zone with
 * `zoneLabel()`.
 *
 * Times OUTSIDE bookings follow the same rule through `deviceClock()` /
 * `deviceZoneLabel()` (a circle's start, a request's "Posted", "Available
 * again at"); utils/__tests__/zone-labels-sweep.test.ts fails a source file
 * that formats an hour without naming a zone.
 */

/**
 * The join timing is the SERVER's (My Bookings PR A): every booking payload
 * carries `join_shows_at` (the Join area shows, greyed with when it opens:
 * start − 10 min, EmpathyBookingService::JOIN_SHOW_LEAD_MINUTES),
 * `join_opens_at` (the scheduler makes the session: the start) and
 * `joinable` (join()'s own test). The helpers below read those; this ten
 * minutes is only the fallback for an older server that sends none.
 */
export const JOIN_LEAD_MS = 10 * 60 * 1000;

type Timed = Pick<Booking, 'starts_at'> & Partial<Pick<Booking, 'join_shows_at' | 'join_opens_at'>>;

function servedMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** When the Join area shows (greyed): the served `join_shows_at`, else start − 10 min. */
export function joinShowsAtMs(booking: Timed): number {
  return servedMs(booking.join_shows_at) ?? Date.parse(booking.starts_at) - JOIN_LEAD_MS;
}

/** When Join opens (the session is made): the served `join_opens_at`, else the start. */
export function joinOpensAtMs(booking: Timed): number {
  return servedMs(booking.join_opens_at) ?? Date.parse(booking.starts_at);
}

/** The served `join_opens_at` as an ISO instant (the start for an older server). */
function joinOpensIso(booking: Timed): string {
  return servedMs(booking.join_opens_at) !== null ? booking.join_opens_at! : booking.starts_at;
}

/** A valid IANA zone a booking time is shown in. Made only by `shownZone()`. */
export type ShownZone = string & { readonly __shownZone: unique symbol };

interface Parts {
  weekday: string;
  month: string;
  day: string;
  hour: number;
  minute: number;
  /** "YYYY-MM-DD" in the zone. */
  dateKey: string;
  /** "MDT", "UTC", "GMT+5:30": the zone's short name at that instant. */
  label: string;
}

function safeZone(tz: string | null | undefined): string | undefined {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

/**
 * The zone a booking time is shown in: the device's own; else `zoneOfRecord`
 * (the zone stored on the booking or the slots answer) when the device
 * cannot name one; else UTC. Resolve it once per render, not per time: it
 * asks Intl each call, so a zone change under a running app shows on the
 * next render.
 */
export function shownZone(zoneOfRecord?: string | null): ShownZone {
  return (safeZone(deviceTimeZone()) ?? safeZone(zoneOfRecord) ?? 'UTC') as ShownZone;
}

interface ZoneFormatters {
  full: Intl.DateTimeFormat;
  month: Intl.DateTimeFormat;
}

function buildFormatters(zone: string): ZoneFormatters {
  return {
    full: new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'short',
    }),
    month: new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      month: '2-digit',
    }),
  };
}

/**
 * Built once per zone: the booking sheet formats every time a 60-day answer
 * lists (thousands for a listener open all day) on open and on each day tap,
 * and building the formatters per call blocked the main thread for seconds.
 */
const zoneFormatters = new Map<string, ZoneFormatters>();

function formattersFor(zone: string): ZoneFormatters {
  const hit = zoneFormatters.get(zone);
  if (hit) return hit;
  const built = buildFormatters(safeZone(zone) ?? 'UTC');
  zoneFormatters.set(zone, built);
  return built;
}

/**
 * The server's Timezone::label in the app: Intl's short name ("MDT", "EST",
 * "UTC", "GMT"), which en-US already writes as "GMT+2" / "GMT+5:30" for
 * zones it has no letters for. A bare offset ("+0545") or no name at all
 * becomes "GMT±h[:mm]" from the offset, and a zero offset "UTC".
 */
export function shortZoneName(raw: string | undefined, offsetMinutes: number): string {
  if (raw && !/^[+-]?\d/.test(raw)) return raw;
  if (offsetMinutes === 0) return 'UTC';
  const abs = Math.abs(offsetMinutes);
  const minutes = abs % 60;
  return `GMT${offsetMinutes < 0 ? '-' : '+'}${Math.floor(abs / 60)}${
    minutes ? `:${String(minutes).padStart(2, '0')}` : ''
  }`;
}

function parts(iso: string, zone: string): Parts {
  const { full, month } = formattersFor(zone);
  const date = new Date(iso);
  const p: Record<string, string> = {};
  for (const x of full.formatToParts(date)) p[x.type] = x.value;
  const monthNum = month.format(date).padStart(2, '0');
  const hour = Number(p.hour) % 24;
  const minute = Number(p.minute);
  const wallMs = Date.UTC(Number(p.year), Number(monthNum) - 1, Number(p.day), hour, minute);
  const instantMs = Math.floor(date.getTime() / 60_000) * 60_000;
  return {
    weekday: p.weekday,
    month: p.month,
    day: p.day,
    hour,
    minute,
    dateKey: `${p.year}-${monthNum}-${p.day.padStart(2, '0')}`,
    label: shortZoneName(p.timeZoneName, Math.round((wallMs - instantMs) / 60_000)),
  };
}

/**
 * The short name of `zone` at the instant `iso` ("MST" on a November date,
 * "MDT" on a July one). Takes any zone name: the schedule editor labels the
 * listener's own zone with it, never converting their hours.
 */
export function zoneLabel(iso: string, zone: string): string {
  return parts(iso, zone).label;
}

/** The local calendar day of an instant, for grouping slots into Day chips. */
export function dayKey(iso: string, zone: ShownZone): string {
  return parts(iso, zone).dateKey;
}

/** Day chip: { weekday: "Thu", date: "Oct 23" }. */
export function dayChip(iso: string, zone: ShownZone) {
  const p = parts(iso, zone);
  return { weekday: p.weekday, date: `${p.month} ${p.day}` };
}

/** A day key ("2026-10-23") as the Day chips name it: "Fri Oct 23". Zone-free. */
export function dayKeyName(key: string): string {
  const p = parts(`${key}T12:00:00Z`, 'UTC');
  return `${p.weekday} ${p.month} ${p.day}`;
}

function meridiem(hour: number): 'am' | 'pm' {
  return hour < 12 ? 'am' : 'pm';
}

/**
 * "7:30", or "7:30 pm" with the meridiem: a chip under a heading that
 * already names the zone (`zoneLabels`). Anywhere else, `clockWithZone`.
 */
export function clock(iso: string, zone: ShownZone, withMeridiem = true): string {
  const p = parts(iso, zone);
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const text = `${h12}:${String(p.minute).padStart(2, '0')}`;
  return withMeridiem ? `${text} ${meridiem(p.hour)}` : text;
}

/** "7:30 pm MDT". */
export function clockWithZone(iso: string, zone: ShownZone): string {
  return `${clock(iso, zone)} ${zoneLabel(iso, zone)}`;
}

type Instant = string | number | Date;

function isoOf(at: Instant): string | null {
  const d = at instanceof Date ? at : new Date(at);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * The name of this device's zone at `at` ("MDT", "GMT+5:30"; "UTC" when the
 * device cannot name one): the label for a time the app shows on the
 * viewer's own clock. "" for an invalid instant.
 */
export function deviceZoneLabel(at: Instant): string {
  const iso = isoOf(at);
  return iso ? zoneLabel(iso, shownZone()) : '';
}

/**
 * "7:30 pm MDT": an instant on this device's clock, its zone named. The one
 * formatter for a time of day the app shows OUTSIDE a booking (a circle's
 * start, a request's "Posted", "Available again at"): every time shown
 * names its zone (David, 2026-10-30). "" for an invalid instant.
 */
export function deviceClock(at: Instant): string {
  const iso = isoOf(at);
  if (!iso) return '';
  return clockWithZone(iso, shownZone());
}

/**
 * The distinct zone labels of a day's starts, in order: one ("MDT") on any
 * ordinary day, two on the day the clocks change.
 */
export function zoneLabels(isos: string[], zone: ShownZone): string[] {
  return Array.from(new Set(isos.map((i) => zoneLabel(i, zone))));
}

/** True when a day's slots straddle noon, so bare "7:30" chips would be ambiguous. */
export function mixesMeridiem(isos: string[], zone: ShownZone): boolean {
  return new Set(isos.map((i) => meridiem(parts(i, zone).hour))).size > 1;
}

/** "Book Thu Oct 23, 7:30 pm MDT · 30 min" (artboard ProfileBooking). */
export function bookLabel(iso: string, zone: ShownZone, minutes: number): string {
  const p = parts(iso, zone);
  return `Book ${p.weekday} ${p.month} ${p.day}, ${clockWithZone(iso, zone)} · ${minutes} min`;
}

/**
 * "7:30 – 8:00 pm MDT · 30 min": a call's span without its day (My
 * Bookings' rows, under a date block). A call the clocks change during
 * names both ends ("1:30 am MDT – 1:00 am MST").
 */
export function clockRangeLabel(
  startIso: string,
  endIso: string,
  zone: ShownZone,
  minutes: number,
): string {
  const a = parts(startIso, zone);
  const b = parts(endIso, zone);
  if (a.label !== b.label) {
    return `${clockWithZone(startIso, zone)} – ${clockWithZone(endIso, zone)} · ${minutes} min`;
  }
  const from =
    meridiem(a.hour) === meridiem(b.hour) ? clock(startIso, zone, false) : clock(startIso, zone);
  return `${from} – ${clockWithZone(endIso, zone)} · ${minutes} min`;
}

/**
 * "Thu, 7:30 – 8:00 pm MDT · 30 min" (artboard BookingConfirmed). A call the
 * clocks change during names both ends ("1:30 am MDT – 1:00 am MST").
 */
export function rangeLabel(
  startIso: string,
  endIso: string,
  zone: ShownZone,
  minutes: number,
): string {
  return `${parts(startIso, zone).weekday}, ${clockRangeLabel(startIso, endIso, zone, minutes)}`;
}

/** The date tile: { month: "OCT", day: "23" }. */
export function dateTile(iso: string, zone: ShownZone) {
  const p = parts(iso, zone);
  return { month: p.month.toUpperCase(), day: p.day };
}

export type JoinState =
  /** Not yet ten minutes before the start, cancelled, or over. */
  | 'none'
  /** In the window, the scheduler has not made the session yet. */
  | 'starting'
  /** In the window with a session: Join is live. */
  | 'ready';

/**
 * Join is shown from `join_shows_at` (start − 10 min) until the end, and is
 * live once the session exists (the scheduler makes it at `join_opens_at`).
 * A session the server says is not `joinable` has ended: no Join, not a
 * wait. The server's join route is still the judge: this decides only what
 * the button looks like.
 */
export function joinState(
  booking: Pick<Booking, 'status' | 'starts_at' | 'ends_at' | 'session_id' | 'session_live'> &
    Partial<Pick<Booking, 'join_shows_at' | 'join_opens_at' | 'joinable'>>,
  nowMs: number,
): JoinState {
  if (booking.status === 'cancelled') return 'none';
  // Closed by the no-show check (completed / no_show): the outcome is decided,
  // but while its session still runs a late person can get in (D49, #989) —
  // the page shows the closed heading AND Join. Once the call has ended (or
  // there never was one), no Join and no "opens at …".
  if (booking.status !== 'confirmed' && !booking.session_live) return 'none';
  const shows = joinShowsAtMs(booking);
  const end = Date.parse(booking.ends_at);
  if (Number.isNaN(shows) || Number.isNaN(end)) return 'none';
  if (nowMs < shows || nowMs >= end) return 'none';
  if (!booking.session_id) return 'starting';
  return booking.joinable === false ? 'none' : 'ready';
}

/** Milliseconds until joinState can next change (for one timer), or null. */
export function nextJoinChangeIn(
  booking: Pick<Booking, 'status' | 'starts_at' | 'ends_at'> &
    Partial<Pick<Booking, 'join_shows_at'>>,
  nowMs: number,
): number | null {
  if (booking.status === 'cancelled') return null;
  const open = joinShowsAtMs(booking);
  const end = Date.parse(booking.ends_at);
  if (nowMs < open) return open - nowMs;
  if (nowMs < end) return end - nowMs;
  return null;
}

/**
 * Milliseconds until the "in N min" countdown next changes, while the Join
 * area waits before the start; null outside that stretch. Minutes are
 * rounded UP ("in 5 min" from 5:00 down to 4:01), so the line changes on
 * each whole minute before the start and the last change is the start itself.
 */
export function nextCountdownTickIn(
  booking: Pick<Booking, 'status' | 'starts_at'> &
    Partial<Pick<Booking, 'join_shows_at' | 'join_opens_at'>>,
  nowMs: number,
): number | null {
  if (booking.status === 'cancelled') return null;
  const opens = joinOpensAtMs(booking);
  if (Number.isNaN(opens) || nowMs < joinShowsAtMs(booking) || nowMs >= opens) return null;
  return (opens - nowMs) % 60_000 || 60_000;
}

/** Whole minutes until Join opens, rounded up; 0 once it has passed. */
function minutesUntil(booking: Timed, nowMs: number): number {
  return Math.max(0, Math.ceil((joinOpensAtMs(booking) - nowMs) / 60_000));
}

type OpensBooking = Timed & Pick<Booking, 'timezone'>;

/**
 * The waiting Join's line: "Opens at 7:30 pm MDT · in 5 min" (the served
 * `join_opens_at`), and "· opening now" from then until the scheduler has
 * made the session (it runs each minute).
 */
export function opensLine(booking: OpensBooking, nowMs: number): string {
  const n = minutesUntil(booking, nowMs);
  return `Opens at ${clockWithZone(
    joinOpensIso(booking),
    shownZone(booking.timezone),
  )} · ${n > 0 ? `in ${n} min` : 'opening now'}`;
}

/**
 * The same line for screen readers: words, not the dot and "min". Read
 * when someone reaches it, never announced (no live region: it would speak
 * every minute).
 */
export function opensLineSpoken(booking: OpensBooking, nowMs: number): string {
  const n = minutesUntil(booking, nowMs);
  const when = n > 0 ? `in ${n} minute${n === 1 ? '' : 's'}` : 'opening now';
  return `Opens at ${clockWithZone(joinOpensIso(booking), shownZone(booking.timezone))}, ${when}.`;
}

/** The booked-call screen's route. Push and email deep links land here. */
export function bookingPath(id: number | string, join = false): string {
  return `/app/bookings/${id}${join ? '?join=1' : ''}`;
}

/**
 * The 24 h minimum (EmpathyBookingService::MIN_NOTICE_HOURS). The slots
 * answer carries it as `min_notice_hours`; this is for copy shown where no
 * slots answer exists (the schedule page). Pinned to the server by utils/__tests__/booking-min-notice.test.ts.
 */
export const BOOKING_MIN_NOTICE_HOURS = 24;

/**
 * One zone, whatever it is called: V8 names an Indian device "Asia/Calcutta"
 * while the server stores the canonical "Asia/Kolkata" (Timezone::canonicalize),
 * so the names are compared as Intl resolves them, in the same engine.
 */
export function sameZone(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    const id = (zone: string) =>
      new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone;
    return id(a) === id(b);
  } catch {
    return false;
  }
}

/**
 * The schedule editor's zone line. A listener's weekly hours are wall-clock
 * hours in THEIR schedule's zone and are never converted, so the line names
 * that zone with its label now; when this device is elsewhere it says so,
 * rather than calling the schedule's zone "your time zone".
 */
export function scheduleZoneLine(
  scheduleZone: string,
  nowMs: number,
  deviceZone: string | null = deviceTimeZone(),
): string {
  const at = new Date(nowMs).toISOString();
  const label = zoneLabel(at, scheduleZone);
  // "UTC (UTC)" says one thing twice.
  const named = label === scheduleZone ? scheduleZone : `${scheduleZone} (${label})`;
  if (!deviceZone || sameZone(deviceZone, scheduleZone)) {
    return `Times in your time zone, ${named}.`;
  }
  return `Times in ${named}, your schedule's time zone, not this device's (${zoneLabel(
    at,
    deviceZone,
  )}). They are not converted.`;
}

/**
 * R5 (David, 2026-10-19): the booking rules in ONE plain line under the
 * hours: what others can book, the 24 h minimum, the lengths. Who may book is
 * the server's rule (EmpathyBookingService::eligibility), so the line says
 * "Others", not a group condition; the lengths follow the draft as toggled.
 */
export function bookingRulesLine(durations: readonly number[]): string {
  const sorted = [...durations].sort((a, b) => a - b).map(String);
  const lengths =
    sorted.length <= 1
      ? (sorted[0] ?? '')
      : `${sorted.slice(0, -1).join(', ')} or ${sorted[sorted.length - 1]}`;
  return `Others can book any free slot in these hours, at least ${BOOKING_MIN_NOTICE_HOURS} hours ahead, for ${lengths} minutes.`;
}
